import { useGlobalModalContext, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { forwardRef, ReactElement, useMemo } from 'react';
import { Accordion } from 'semantic-ui-react';
import { AccordionWidgetData } from '../../../models/RiskProducts';
import { DATE_FORMATS, formatDate } from '../../../utils/dateUtils';
import ExpandableMetric from '../common/ExpandableMetric';
import styles from '../AcordionList.module.scss';
import { getRiskOverviewData } from './RiskOverviewWidget.logic';
import { CALCULATION_DATE, DATA_DATE, MODEL } from './RiskOverviewWidget.constants';
import { RiskOverviewProps } from './RiskOverviewWidget.types';
import { GenericModalProps } from '../../GlobalModal/GenericModal/GenericModal';
import { MODAL_TYPES } from '../../GlobalModal';

const cx = classNames.bind(styles);

const RiskOverviewWidget = (
  { riskOverviewMetrics, isError, onErrorRetry, ...sortableWidgetProps }: RiskOverviewProps,
  ref
): ReactElement | null => {
  const riskOverviewData = useMemo(() => {
    return getRiskOverviewData(riskOverviewMetrics);
  }, [riskOverviewMetrics]);
  const { showModal } = useGlobalModalContext();

  const areChildrenEmpty = (metric: AccordionWidgetData): boolean => {
    return metric.children.every((item) => item.value == null);
  };

  const renderOverview = () => {
    return riskOverviewData.map((metric) => {
      return (
        <ExpandableMetric
          key={`overview-${metric.item.label}`}
          isItemExpanded={!areChildrenEmpty(metric)}
          metric={metric}
        />
      );
    });
  };

  const renderModelContent = () => {
    return (
      <div className={cx('notes')}>
        <div className={cx('text')}>
          {CALCULATION_DATE}
          <span className={cx('info')}>
            {formatDate(riskOverviewMetrics?.calc_date, DATE_FORMATS.MONTH_ABBR_DATE, '-')}
          </span>
        </div>
        <div className={cx('text')}>
          {DATA_DATE}
          <span className={cx('info')}>
            {formatDate(riskOverviewMetrics?.data_date, DATE_FORMATS.MONTH_ABBR_DATE, '-')}
          </span>
        </div>
        <div className={cx('text')}>
          {MODEL}
          <span className={cx('info')}>{riskOverviewMetrics?.model_sector || '-'}</span>
        </div>
      </div>
    );
  };

  const onClick = () => {
    const modalProps: GenericModalProps = {
      headerTitle: 'Risk Overview',
      content: `<p>CreditSights’ Risk Products platform simplifies credit investment decisions and risk monitoring, providing quantitatively-based risk insights for each issuer.  This section provides CreditSights’ objective proprietary near-term and medium-term credit quality assessments for this company, including:<p>
        <ul>
        <li><b>Credit Quality Score (CQS)</b> – a medium-term standalone and objective assessment that has proven to be an effective leading indicator of agency rating actions. Incorporates fundamental (~80% weighting) and equity market information (~20%) to derive the model outputs. </li>        
        <li><b>Credit Risk Estimate (CRE)</b> – a near-term risk model that produces a 1-year forward prediction of default. Our CRE model uses a hybrid approach that combines fundamental (approx. 30% weight) and equity market information (approx. 70% weight).</li>
        <li><b>Fallen Angel Score (FAS)</b> – a predictive model that incorporates fundamental, equity market and bond market information to produce the predicted likelihood of a BBB issuer being downgraded to high yield over the next 12 months.</li>
        </ul>
        <p>For more detail on our datasets and models underlying these scores, please review our whitepapers located (<a href="/page/rp_about" target="_blank">here</a>)<br>
        
        </p>`,
      dataTestId: 'fundamentals-modal',
    };
    showModal(MODAL_TYPES.GENERIC, modalProps);
  };

  return (
    <Widget
      headerTitle="Risk Overview"
      headerTooltip={{
        tooltipText: 'More on Risk',
        onClick,
      }}
      dataTestId="risk-overview-widget"
      isError={isError}
      onRetryAfterErrorClick={onErrorRetry}
      ref={ref}
      {...sortableWidgetProps}
    >
      <div className={cx('body')} data-testid="risk-overview-content">
        <div className={cx('containers')}>
          <Accordion exclusive={false}>{renderOverview()}</Accordion>
          <div className={cx('notes-container')}>{renderModelContent()}</div>
        </div>
      </div>
    </Widget>
  );
};

export default forwardRef(RiskOverviewWidget);
